/**
 * Resolução de role conforme tipo da empresa (tenant).
 * Canal = propriedade do tenant, não escolha livre do usuário.
 */

export type TenantTipoUnidade = 'MATRIZ' | 'ASSOCIADO' | 'REPRESENTACAO';

export const TENANT_TIPO_UNIDADE_LABELS: Record<TenantTipoUnidade, string> = {
  MATRIZ: 'Matriz',
  ASSOCIADO: 'Associado (MEI)',
  REPRESENTACAO: 'Representação (franquia)',
};

/** Tipos disponíveis ao criar empresa pelo superadmin (matriz é fixa no tenant 1) */
export const TENANT_TIPO_CRIACAO_OPTIONS = [
  { value: 'ASSOCIADO' as const, label: TENANT_TIPO_UNIDADE_LABELS.ASSOCIADO },
  { value: 'REPRESENTACAO' as const, label: TENANT_TIPO_UNIDADE_LABELS.REPRESENTACAO },
];

const ROLES_OPERACIONAIS_REPRESENTACAO = [
  'SUPERVISOR',
  'RELACIONADOR',
  'LIDER_VENDAS',
  'POS_VENDA',
  'SECRETARIA',
] as const;

const ROLES_MATRIZ = [
  'SUPER_ADMIN',
  'SECRETARIA',
  'SUPERVISOR',
  'RELACIONADOR',
  'LIDER_VENDAS',
  'POS_VENDA',
] as const;

export function resolveRoleForTenant(
  tipoUnidade: TenantTipoUnidade | null | undefined,
  options: {
    requestedRole?: string | null;
    activeUsersInTenant: number;
  }
): string {
  const { requestedRole, activeUsersInTenant } = options;

  if (!tipoUnidade || tipoUnidade === 'MATRIZ') {
    return requestedRole || 'USER';
  }

  if (tipoUnidade === 'ASSOCIADO') {
    return 'ASSOCIADO';
  }

  // REPRESENTACAO — primeiro usuário = admin da franquia
  if (activeUsersInTenant === 0) {
    return 'REPRESENTANTE';
  }

  if (
    requestedRole &&
    ROLES_OPERACIONAIS_REPRESENTACAO.includes(requestedRole as (typeof ROLES_OPERACIONAIS_REPRESENTACAO)[number])
  ) {
    return requestedRole;
  }

  return requestedRole || 'RELACIONADOR';
}

export function getAssignableRolesForTenant(
  tipoUnidade: TenantTipoUnidade | null | undefined,
  activeUsersInTenant: number
): readonly string[] {
  if (tipoUnidade === 'ASSOCIADO') {
    return ['ASSOCIADO'];
  }

  if (tipoUnidade === 'REPRESENTACAO') {
    if (activeUsersInTenant === 0) {
      return ['REPRESENTANTE'];
    }
    return ['REPRESENTANTE', ...ROLES_OPERACIONAIS_REPRESENTACAO];
  }

  return ROLES_MATRIZ;
}

export function isRoleLockedForTenant(
  tipoUnidade: TenantTipoUnidade | null | undefined,
  activeUsersInTenant: number
): boolean {
  return tipoUnidade === 'ASSOCIADO' || (tipoUnidade === 'REPRESENTACAO' && activeUsersInTenant === 0);
}

export async function countActiveUsersInTenant(tenantId: string | number): Promise<number> {
  const { supabase } = await import('@/integrations/supabase/client');
  const { count, error } = await supabase
    .from('profiles')
    .select('id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('status', 'ACTIVE')
    .is('deleted_at', null);

  if (error) throw error;
  return count ?? 0;
}

export async function fetchTenantTipoUnidade(tenantId: string | number): Promise<TenantTipoUnidade | null> {
  const { supabase } = await import('@/integrations/supabase/client');
  const { data, error } = await supabase
    .from('tenants')
    .select('tipo_unidade')
    .eq('id', tenantId)
    .single();

  if (error) throw error;
  return (data?.tipo_unidade as TenantTipoUnidade) ?? null;
}

export async function resolveRoleForTenantId(
  tenantId: string | number,
  requestedRole?: string | null
): Promise<string> {
  const [tipo, activeUsers] = await Promise.all([
    fetchTenantTipoUnidade(tenantId),
    countActiveUsersInTenant(tenantId),
  ]);
  return resolveRoleForTenant(tipo, { requestedRole, activeUsersInTenant: activeUsers });
}
